import { Component } from '@theme/component';
import { normalizeString } from '@theme/utilities';
/**
 * A custom element that displays a localization form.
 *
 * @typedef {object} FormRefs
 * @property {HTMLDivElement} results - The list element.
 * @property {HTMLInputElement} input - The country input element.
 * @property {HTMLUListElement[]} listItems - The country list items element.
 * @property {HTMLFormElement} form - The form element.
 * @property {HTMLDivElement} liveRegion - The live region element.
 * @property {HTMLSpanElement} noResultsMessage - The no results message element.
 * @property {HTMLInputElement} search - The search input element.
 * @property {HTMLButtonElement} resetButton - The reset button element.
 *
 * @extends {Component<FormRefs>}
 */

class LocalizationFormComponent extends Component {

  connectedCallback() {
    super.connectedCallback();

    // Fix return_to if form was rendered inside a lazy-loaded context (e.g. menu-loader view)
    const returnToInput = this.querySelector('input[name="return_to"]');
    if (returnToInput?.value.includes('view=menu-loader')) {
      returnToInput.value = window.location.pathname + window.location.search;
    }

    if (this.refs.search) {
      this.refs.search.addEventListener('keydown', this.#onSearchKeyDown);
    }
    if (this.refs.results) {
      this.refs.results.addEventListener('keydown', this.#onContainerKeyDown);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.refs.search) {
      this.refs.search.removeEventListener('keydown', this.#onSearchKeyDown);
    }
    if (this.refs.results) {
      this.refs.results.removeEventListener('keydown', this.#onContainerKeyDown);
    }
  }

  get context() {
    return this.dataset.context || 'header';
  }

  /**
   * Handles form submission with selected country value
   * @param {string} value - Selected country value
   * @param {Event} event - Submit event
   */
  onSubmit = (value, event) => {
    event.preventDefault();
    const { input, form } = this.refs;

    input.value = value;

    form?.submit();

    const parentDialog = this.closest('dropdown-dialog');
    if (parentDialog) {
      parentDialog.hideResults();
    }
  }

  /**
   * Handles the keydown event for the container.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onContainerKeyDown = (event) => {
    const { input, listItems, form } = this.refs;

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus('UP');
        break;
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus('DOWN');
        break;
      case 'Enter':
        event.preventDefault();
        event.stopPropagation();
        const focusedItem = listItems.find((item) => item.getAttribute('aria-selected') === 'true');

        if (focusedItem) {
          input.value = focusedItem.dataset.value ?? '';
          form.submit();
        }
        break;
    }

    if (!this.refs.search) return;

    setTimeout(() => {
      const focusableItems = this.refs.listItems.filter((item) => !item.hasAttribute('hidden'));
      const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
      const focusedItem = focusableItems[focusedItemIndex];

      if (focusedItem) {
        this.refs.search.setAttribute('aria-activedescendant', focusedItem.id);
      } else {
        this.refs.search.setAttribute('aria-activedescendant', '');
      }
    });
  };

  /**
   * Finds matches for a given search value in a country element.
   *
   * @typedef {Object} Options
   * @property {boolean} [matchLabel] - Whether to match the label.
   * @property {boolean} [matchAlias] - Whether to match the alias.
   * @property {boolean} [matchIso] - Whether to match the iso.
   * @property {boolean} [matchCurrency] - Whether to match the currency.
   * @property {boolean} [labelMatchStart] - Whether to match the label start.
   * @property {boolean} [aliasExactMatch] - Whether to match the alias exact match.
   *
   * @typedef {Object} MatchTypes
   * @property {boolean} [label] - Whether the label matches the search value.
   * @property {boolean} [alias] - Whether the alias matches the search value.
   * @property {boolean} [iso] - Whether the iso matches the search value.
   * @property {boolean} [currency] - Whether the currency matches the search value.
   *
   * @param {string} searchValue - The search value to find matches for.
   * @param {HTMLElement} countryEl - The country element to find matches in.
   * @param {Options} options - The options for the search.
   * @returns {MatchTypes} The matches found in the country element.
   */
  #findMatches(
    searchValue,
    item,
    options = {
      // Which data types (label, alias, iso) to match against
      matchLabel: true,
      matchAlias: true,
      matchIso: true,
      matchCurrency: true,
      // If true, the search value must match the start of the label
      labelMatchStart: false,
      // If true, a result will not display unless the search value equals an alias in its entirety
      aliasExactMatch: false,
    }
  ) {
    let matchTypes = {};
    const { aliases, value: iso } = item.dataset;

    if (options.matchLabel) {
      const name = normalizeString(item.querySelector('.localization-form__name')?.textContent ?? '');

      if (!name) return matchTypes;

      matchTypes.label = options.labelMatchStart
        ? name.startsWith(searchValue)
        : name.includes(searchValue);
    }

    if (options.matchCurrency) {
      const currency = normalizeString(item.querySelector('.localization-form__currency')?.textContent ?? '');
      matchTypes.currency = currency.includes(searchValue);
    }

    if (options.matchIso) {
      matchTypes.iso = normalizeString(iso ?? '') == searchValue;
    }

    if (options.matchAlias) {
      const countryAliases = aliases?.split(',').map((alias) => normalizeString(alias));

      if (!countryAliases) return matchTypes;

      matchTypes.alias =
        countryAliases.length > 0 &&
        countryAliases.find((alias) =>
          options.aliasExactMatch ? alias === searchValue : alias.startsWith(searchValue)
        ) !== undefined;
    }

    return matchTypes;
  }

  /**
   * Highlights matching text in a string by wrapping it in <mark> tags.
   *
   * @param {string | null} text - The text to highlight.
   * @param {string} searchValue - The search value to highlight.
   * @returns {string} The text with matching parts wrapped in <mark> tags.
   */
  #highlightMatches(text, searchValue) {
    if (!text || !searchValue) return text ?? '';

    const normalizedText = normalizeString(text);
    const normalizedSearch = normalizeString(searchValue);
    const startIndex = normalizedText.indexOf(normalizedSearch);

    if (startIndex === -1) return text;

    const endIndex = startIndex + normalizedSearch.length;
    const before = text.slice(0, startIndex);
    const match = text.slice(startIndex, endIndex);
    const after = text.slice(endIndex);

    return `${before}<mark>${match}</mark>${after}`;
  }

  /**
   * Filters the countries based on the search value.
   */
  /**
   * Filters countries based on search input
   * Highlights matches, updates live region, and shows/hides reset button
   */
  filter() {
    const { filterWrapper, results, listItems, liveRegion, noResultsMessage, resetButton, search } =
      this.refs;
    const { labelResultsCount } = this.dataset;
    const searchValue = normalizeString(search.value);
    let countVisibleCountries = 0;

    if(!this.fixedHeight) {
      const componentHeight = this.clientHeight;
      this.style.setProperty('--localization-height', `${componentHeight}px`);
      this.fixedHeight = true;
    }

    resetButton.toggleAttribute('hidden', !searchValue);
    
    if (filterWrapper) {
      const searchIcon = filterWrapper.querySelector('.localization-form__icon');
      filterWrapper.classList.toggle('is-searching', !!searchValue);
      searchIcon?.toggleAttribute('hidden', searchValue);
    }

    for (const item of listItems) {
      if (searchValue === '') {
        item.removeAttribute('hidden');
        const countrySpan = item.querySelector('.localization-form__name');
        if (countrySpan) {
          countrySpan.textContent = countrySpan.textContent;
        }
        countVisibleCountries++;
      } else {
        const matches = this.#findMatches(searchValue, item);

        // In the future, we could reorder/rank filtered results based on the match types
        if (matches.label || matches.alias || matches.iso || matches.currency) {
          item.removeAttribute('hidden');
          const countrySpan = item.querySelector('.localization-form__name');
          if (countrySpan) {
            countrySpan.innerHTML = this.#highlightMatches(countrySpan.textContent, searchValue);
          }
          countVisibleCountries++;
        } else {
          item.setAttribute('hidden', '');
        }
      }
    }

    if (liveRegion && labelResultsCount) {
      liveRegion.innerText = labelResultsCount.replace('[count]', `${countVisibleCountries}`);
    }

    noResultsMessage.hidden = countVisibleCountries > 0;
    results.scrollTop = 0;
  }

  /**
   * Changes the focus of the country list items.
   *
   * @param {string} direction - The direction to change the focus.
   */
  #changeCountryFocus(direction) {
    const { listItems } = this.refs;
    const focusableItems = listItems.filter((item) => !item.hasAttribute('hidden'));
    const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
    const focusedItem = focusableItems[focusedItemIndex];
    let itemToFocus;

    if (direction === 'UP') {
      itemToFocus =
        focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
    } else {
      itemToFocus =
        focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
    }

    if (focusedItem) {
      focusedItem.setAttribute('aria-selected', 'false');
    }
    itemToFocus?.setAttribute('aria-selected', 'true');
    itemToFocus?.focus();
  }

  /**
   * Resets the countries filter.
   *
   * @param {Event} event - The event object.
   */
  reset(event) {
    const { search } = this.refs;

    event.stopPropagation();
    search.value = '';
    this.filter();
    search.setAttribute('aria-activedescendant', '');
    search.focus();
  }

  /**
   * Handles the keydown event for the search input.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.#onContainerKeyDown(event);
  };

  /**
   * Resets the form.
   */
  resetForm() {
    const { search } = this.refs;

    if (!search) return;

    if (search.value != '') {
      search.value = '';
      this.filter();
      search.setAttribute('aria-activedescendant', '');
    }
  }

  /**
   * Focuses the search input.
   */
  focusSearchInput = () => {
    const { search } = this.refs;

    search?.focus();
  };
}

if (!customElements.get('localization-form-component')) {
  customElements.define('localization-form-component', LocalizationFormComponent);
}